import type {
    TcrAttClassBucket,
    TcrBrandIdentityStatus,
    TcrCampaign,
    TcrCampaignStatus,
    TcrEntityType,
    TcrScoreBucket,
    TcrSource,
    TcrTermsState,
    TcrTmobileBrandTier,
    TcrTmobileTierState,
    TcrUseCase,
    TcrVendor,
    TcrVettingState,
} from '~/services/adminTenDlcClient.types';
import { shortDateTime } from '~/utils/date';

export const sourceLabels: Record<TcrSource, string> = {
    tcr: 'TCR',
    twilio: 'Twilio',
};

export const vendorLabels: Record<TcrVendor, string> = {
    bandwidth: 'Bandwidth',
    twilio: 'Twilio',
};

export const entityTypeLabels: Record<TcrEntityType, string> = {
    government: 'Government',
    non_profit: 'Non-Profit',
    private_profit: 'Private Company',
    public_profit: 'Publicly Traded Company',
    sole_proprietor: 'Sole Proprietor',
};

export const campaignStatusLabels: Record<TcrCampaignStatus, string> = {
    active: 'Active',
    expired: 'Expired',
    failed: 'Failed',
    pending: 'Pending',
};

export const dcaCompletion = (
    campaign: Pick<TcrCampaign, 'source' | 'status' | 'sharing_status' | 'dca_completed_at'>
): { label: string; color: 'success' | 'danger' | 'amber' | 'slate'; title: string } | null => {
    if ('tcr' !== campaign.source) {
        return null;
    }

    if (null !== campaign.dca_completed_at) {
        return { label: 'Yes', color: 'success', title: `Recorded ${shortDateTime(campaign.dca_completed_at)}` };
    }

    if ('active' !== campaign.status) {
        return { label: 'No', color: 'slate', title: 'The campaign is not active at TCR' };
    }

    switch (campaign.sharing_status) {
        case 'declined':
            return {
                label: 'No · declined',
                color: 'danger',
                title: 'Bandwidth declined the campaign; open it for the reasons',
            };
        case 'pending':
            return {
                label: 'No · Bandwidth review',
                color: 'amber',
                title: 'Waiting on Bandwidth to accept the share',
            };
        case 'accepted':
            return {
                label: 'No · DCA review',
                color: 'amber',
                title: "Bandwidth accepted the campaign; its vetting partner's review usually takes 3 to 5 business days",
            };
        case 'unshared':
            return {
                label: 'No · not shared',
                color: 'danger',
                title: 'Not shared to Bandwidth; open the campaign to share it',
            };
        default:
            return {
                label: 'No · sharing unknown',
                color: 'slate',
                title: "Voxie hasn't read the Bandwidth sharing status yet",
            };
    }
};

// TCR's own display names, from GET /v2/enum/usecase.
export const useCaseLabels: Record<TcrUseCase, string> = {
    '2fa': '2FA',
    account_notification: 'Account Notification',
    agents_franchises: 'Agents and Franchises',
    carrier_exempt: 'Carrier Exemptions',
    charity: 'Charity',
    customer_care: 'Customer Care',
    delivery_notification: 'Delivery Notification',
    emergency: 'Emergency',
    fraud_alert: 'Fraud Alert Messaging',
    higher_education: 'Higher Education',
    k12_education: 'K-12 Education',
    low_volume: 'Low Volume Mixed',
    m2m: 'Machine to Machine',
    marketing: 'Marketing',
    mixed: 'Mixed',
    political: 'Political',
    polling_voting: 'Polling and voting',
    proxy: 'Proxy',
    public_safety_restricted: 'Public Safety (Restricted)',
    public_service_announcement: 'Public Service Announcement',
    security_alert: 'Security Alert',
    social: 'Social',
    sole_proprietor: 'Sole Proprietor',
    sweepstake: 'Sweepstake',
    trial: 'Platform Free Trial',
    ucaas_high: 'UCaaS High Volume',
    ucaas_low: 'UCaaS Low Volume',
};

export const verifiedLabel = (verified: boolean | null): string => (true === verified ? 'Verified' : 'Unverified');

export const brandVerificationLabel = (
    verified: boolean | null,
    identityStatus: TcrBrandIdentityStatus | null
): string => ('pending' === identityStatus ? 'Verifying' : verifiedLabel(verified));

export const verificationLabel = (verified: boolean | null, vettingScore: number | null): string => {
    if (null === vettingScore) {
        return verifiedLabel(verified);
    }

    return `${verifiedLabel(verified)} · ${vettingScore}`;
};

// AT&T 10DLC per-campaign throughput tiers by message class.
const messageClassDetails: Record<string, string> = {
    A: '4,500 SMS/min toward AT&T · top vetting tier (score 75–100), dedicated use case',
    B: '4,500 SMS/min toward AT&T · top vetting tier (score 75–100), mixed/marketing',
    C: '2,400 SMS/min toward AT&T · mid vetting tier (score 50–74), dedicated use case',
    D: '2,400 SMS/min toward AT&T · mid vetting tier (score 50–74), mixed/marketing',
    E: '240 SMS/min toward AT&T · low vetting tier (score 1–49), dedicated use case',
    F: '240 SMS/min toward AT&T · low vetting tier (score 1–49), mixed/marketing',
    G: '60 SMS/min toward AT&T · group messaging',
    K: '4,500 SMS/min toward AT&T · election',
    N: '60 SMS/min toward AT&T · agents/local',
    P: '2,400 SMS/min toward AT&T · charity',
    S: '9,000 SMS/min toward AT&T · social — Voxie paces it at 4,500',
    T: '75 SMS/min toward AT&T · low-volume mixed campaigns',
    W: '15 SMS/min toward AT&T · sole proprietor',
    X: '4,500 SMS/min toward AT&T · emergency',
    Y: '3,000 SMS/min toward AT&T · trusted exemption',
    Z: '6 SMS/min toward AT&T · trial',
};

export const messageClassDetail = (messageClass: string): string | null => messageClassDetails[messageClass] ?? null;

export const tmobileBrandTierLabels: Record<TcrTmobileBrandTier, string> = {
    low: 'Low',
    lower_mid: 'Lower mid',
    top: 'Top',
    uncapped: 'Uncapped',
    upper_mid: 'Upper mid',
};

export const termsLabel = (betterTermsAvailable: boolean | null): string => {
    if (null === betterTermsAvailable) {
        return '—';
    }

    return betterTermsAvailable ? 'Better available' : 'Up to date';
};

// Only the class and tier are stored, so other improvements leave this empty.
export const availableTermsLabel = (
    campaign: Pick<
        TcrCampaign,
        'att_message_class' | 'available_att_message_class' | 'tmobile_brand_tier' | 'available_tmobile_brand_tier'
    >
): string => {
    const changes: string[] = [];

    if (
        null !== campaign.available_att_message_class &&
        campaign.available_att_message_class !== campaign.att_message_class
    ) {
        changes.push(`AT&T ${campaign.att_message_class ?? '—'} → ${campaign.available_att_message_class}`);
    }

    if (
        null !== campaign.available_tmobile_brand_tier &&
        campaign.available_tmobile_brand_tier !== campaign.tmobile_brand_tier
    ) {
        const current = campaign.tmobile_brand_tier ? tmobileBrandTierLabels[campaign.tmobile_brand_tier] : '—';

        changes.push(`T-Mobile ${current} → ${tmobileBrandTierLabels[campaign.available_tmobile_brand_tier]}`);
    }

    return changes.join(', ');
};

export const vettingStateOptions: { label: string; value: TcrVettingState }[] = [
    { label: 'Vetted', value: 'vetted' },
    { label: 'Pending', value: 'pending' },
    { label: 'Not vetted', value: 'not_vetted' },
];

// Identifiers start with a letter because the query-string parser coerces numeric-looking values.
export const scoreBucketOptions: { label: string; value: TcrScoreBucket }[] = [
    { label: '75 – 100', value: 'score_75_100' },
    { label: '50 – 74', value: 'score_50_74' },
    { label: '25 – 49', value: 'score_25_49' },
    { label: '0 – 24', value: 'score_0_24' },
    { label: 'Unscored', value: 'score_unscored' },
];

export const attClassBucketOptions: { label: string; value: TcrAttClassBucket }[] = [
    { label: 'A, B, K, S, X', value: 'class_top' },
    { label: 'Y', value: 'class_high' },
    { label: 'C, D, P', value: 'class_mid' },
    { label: 'E, F', value: 'class_low' },
    { label: 'G, N, T, W, Z', value: 'class_minimal' },
    { label: 'Unknown', value: 'class_unsynced' },
];

export const tmobileTierStateOptions: { label: string; value: TcrTmobileTierState }[] = [
    { label: tmobileBrandTierLabels.low, value: 'low' },
    { label: tmobileBrandTierLabels.lower_mid, value: 'lower_mid' },
    { label: tmobileBrandTierLabels.upper_mid, value: 'upper_mid' },
    { label: tmobileBrandTierLabels.top, value: 'top' },
    { label: tmobileBrandTierLabels.uncapped, value: 'uncapped' },
    { label: 'Unknown', value: 'unknown' },
];

export const termsStateOptions: { label: string; value: TcrTermsState }[] = [
    { label: 'Better available', value: 'better' },
    { label: 'Up to date', value: 'current' },
    { label: 'Unknown', value: 'unknown' },
];
